import * as React from "react"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { Button } from "./button"
import { cn } from "../../lib/utils"

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
]

/**
 * MonthYearPicker - Shadcn-style month/year picker
 * @param {Date} value - Selected date
 * @param {Function} onChange - Callback when date changes
 * @param {Date} minDate - Minimum selectable date
 * @param {Date} maxDate - Maximum selectable date
 */
export function MonthYearPicker({ value, onChange, minDate, maxDate, className }) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [displayYear, setDisplayYear] = React.useState(value?.getFullYear() || new Date().getFullYear())
  const currentMonth = value?.getMonth() || new Date().getMonth()
  const currentYear = value?.getFullYear() || new Date().getFullYear()

  const minYear = minDate?.getFullYear() || 2017
  const maxYear = maxDate?.getFullYear() || new Date().getFullYear()

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(displayYear, monthIndex, 1)
    
    // Check if date is within bounds
    if (minDate && newDate < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      return
    }
    if (maxDate && newDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return
    }
    
    onChange(newDate)
    setIsOpen(false)
  }

  const handlePrevYear = () => {
    if (displayYear > minYear) {
      setDisplayYear(displayYear - 1)
    }
  }

  const handleNextYear = () => {
    if (displayYear < maxYear) {
      setDisplayYear(displayYear + 1)
    }
  }

  const isMonthDisabled = (monthIndex) => {
    const testDate = new Date(displayYear, monthIndex, 1)
    if (minDate && testDate < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
      return true
    }
    if (maxDate && testDate > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
      return true
    }
    return false
  }

  const isMonthSelected = (monthIndex) => {
    return monthIndex === currentMonth && displayYear === currentYear
  }

  const formattedValue = value 
    ? `${months[value.getMonth()]} ${value.getFullYear()}`
    : 'Select month'

  return (
    <div className={cn("relative", className)}>
      {/* Trigger Button */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between text-left font-normal"
      >
        <span>{formattedValue}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Picker */}
          <div className="absolute right-0 z-50 mt-2 w-56 rounded-md border bg-popover p-3 shadow-lg">
            {/* Year Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handlePrevYear}
                disabled={displayYear <= minYear}
                className="h-7 w-7 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm font-semibold">
                {displayYear}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleNextYear}
                disabled={displayYear >= maxYear}
                className="h-7 w-7 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Month Grid */}
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, index) => {
                const disabled = isMonthDisabled(index)
                const selected = isMonthSelected(index)
                
                return (
                  <button
                    key={month}
                    type="button"
                    onClick={() => !disabled && handleMonthSelect(index)}
                    disabled={disabled}
                    className={cn(
                      "rounded-md px-1.5 py-1 text-xs font-medium transition-colors",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      selected && "bg-accent text-white hover:bg-accent hover:text-white",
                      disabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
                    )}
                  >
                    {month}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
